import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';

import {
    BehaviorSubject,
    Observable,
    Subscription,
    combineLatest,
    catchError,
    filter,
    finalize,
    forkJoin,
    map,
    of,
    switchMap,
    take
} from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment-timezone';
import { MenuItem } from 'primeng/api';

import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { SplitButtonCommandEvent } from '../../../Common/Forms/components/SplitButton/split-button.component';
import { FullProfileDialogAnimationsEnum } from '../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { CurationAgentPanelComponent } from '../CurationAgentPanel/curation-agent-panel.component';
import { AdHocAgentManagementPanelComponent } from '../AdHocAgentManagementPanel/ad-hoc-agent-management-panel.component';
import { AdHocReportPanelComponent } from '../AdHocReportPanel/ad-hoc-report-panel.component';
import { AgentSourcePanelComponent } from '../AgentSourcePanel/agent-source-panel.component';
import {
    AgentAdhocSourceResponse,
    AgentAdhocSourceStatusEnum,
    CurationAgentSourceStatusEnum
} from '../../../api/agents/models/curation-agent.api';

import { CardChartComponentHighlightColorEnum } from '../../../Common/Charts/components/CardChart/card-chart.component';
import { chartColors } from '../../../Common/Charts/enums/chart-colors.enum';
import { DataPointModel } from '../../../Common/Charts/models/data-point.model';
import { DateGranularityModel } from '../../../Common/Charts/models/date-granularity.model';
import { ChartSeries } from '../../../Common/Charts/models/chart-series.model';
import { ChartDisplaySettingsModel } from '../../../Common/Charts/models/chart-display-settings.model';
import { ChartStylesModel } from '../../../Common/Charts/models/chart-styles.model';
import { Am5DateValueXYChart } from '../../../Common/Charts/models/Am5DateValueXYChart/am5-date-value-x-y-chart.model';
import { Am5DateValueXYChartConfiguration }
    from '../../../Common/Charts/models/Am5DateValueXYChart/am5-date-value-x-y-chart-configuration.model';
import { Am5DateValueXYChartSettings } from '../../../Common/Charts/models/Am5DateValueXYChart/am5-date-value-x-y-chart-settings.model';
import { ChartSpecificTypesEnum } from '../../../Common/Reports/enums/chart-specific-types.enum';
import { ReportBlockTypeEnum } from '../../../Common/Reports/enums/report-block-type.enum';
import { PREventModel } from '../../../Common/Reports/models/pr-event.model';
import { ChartDisplayElementModel } from '../../../Common/Reports/models/chart-display-element.model';
import { ChartTimeIntervalsEnum } from '../../../Common/Reports/enums/chart-time-intervals.enum';
import { Am5ChartConfigurationService } from '../../../Common/Reports/services/am5-chart-configuration.service';
import { Am5ChartSettingsService } from '../../../Common/Reports/services/am5-chart-settings.service';
import { ReportsChartSeriesConverterService } from '../../../Common/Reports/services/reports-chart-series-converter.service';
import { ReportsDataMappingKeysEnum } from '../../../Common/Reports/models/reports-block-mapping-configuration.model';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { CardChartComponentLayoutEnum } from '../../../Common/Charts/components/CardChart/card-chart.component';
import { SocialChartTooltipService } from '../../../Social/Shared/services/social-chart-tooltip.service';
import { CurationAgentModel } from '../../models/curation-agent.model';
import { CurationAgentSourceModel } from '../../models/curation-agent-source.model';
import {
    AgentUsageModel,
    AgentUsageService,
    SourceTotals,
    UsageBounds,
    emptyActivity
} from '../../services/agent-usage.service';
import { CurationAgentsService, sourceNameKey } from '../../services/curation-agents.service';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { DropdownOptionModel } from '../../../Common/Forms/models/dropdown-option.model';
import { DropdownTypesEnum } from '../../../Common/Forms/enums/dropdown-types.enum';
import { ReportSeriesModel } from '../../../Common/Reports/models/report-series.model';
import { RelativeTimezoneDateTypeEnum } from '../../../api/mentions/enum/relative-timezone-date-type.enum';
import { getMonitoringDateQueryInputTopicOptions } from '../../../Monitor/Shared/models/monitoring-date-query-input-options';
import { defaultSourceTypeIcon, sourceTypeIcons, sourceTypeLabels } from '../../enums/source-icons.const';
import { MonitoringSeriesTypeEnum } from '../../../Monitor/Shared/enums/monitoring-series-type.enum';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';


/** Up to this bucket size the activity chart steps in hours rather than in days */
const hourlyBucketMs: number = 60 * 60 * 1000;

/** One bucket on a trend chart */
interface TrendPoint {
    /** Epoch ms of the bucket's start */
    timestamp: number;
    value: number;
}

/** One line on a trend chart, before it is shaped for amCharts */
interface TrendLine {
    /** Localization key */
    label: string;
    color: string;
    points: TrendPoint[];
}

/** One run of ad hoc curation, as listed under Ad Hoc History */
export interface AdHocCurationEntry {
    /** Epoch ms of the submission */
    date: number;
    curated: number;
    analyzed: number;
    /** Still curating, so the Activity column reports progress rather than a total */
    inProgress: boolean;
    /** The run behind the row, which its menu acts on */
    run: AgentAdhocSourceResponse;
}

/**
 * One agent's dashboard: how much it has looked at and curated over the chosen window. The summary
 * cards are the shared agpr-card-chart, as Social's theme analytics summary uses.
 *
 * Analyzed comes from the agent's quota consumption and curated from the mentions it has saved, so
 * the figures here line up with the account's usage panel and with the mention feed respectively.
 */
@Component({
    selector: 'agpr-agent-dashboard',
    templateUrl: './agent-dashboard.component.html',
    styleUrls: ['./agent-dashboard.component.scss'],
    standalone: false
})
export class AgentDashboardComponent implements OnInit, OnDestroy {
    readonly cardColors = CardChartComponentHighlightColorEnum;
    /** Dates are shown in the viewer's own timezone */
    readonly userTimeZone$: Observable<string> = this.curationAgentsService.userTimeZone$;
    readonly cardLayouts = CardChartComponentLayoutEnum;
    readonly dropdownTypes = DropdownTypesEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly sourceStatus = CurationAgentSourceStatusEnum;
    readonly sourceTypeLabels = sourceTypeLabels;
    readonly sourceTypeIcons = sourceTypeIcons;
    readonly defaultSourceTypeIcon = defaultSourceTypeIcon;
    readonly sourceType = MonitoringSeriesTypeEnum;

    /** Set while the action bar's Refresh is re-reading the page */
    refreshing: boolean = false;
    readonly messageSeverity = messageSeverityEnum;
    /** What the cards' change bubbles name their metric as - the card appends the sentence */
    readonly metricTooltipPrefix: string = 'AutomationAgents.agent.dashboard.metric';

    /**
     * The full monitoring range list - relative windows, named periods and Custom - minus All,
     * which the usage figures can't report over. The mention feed and analytics still offer it.
     */
    readonly rangeOptions: DropdownOptionModel[] = getMonitoringDateQueryInputTopicOptions();

    selectedRange: DateQueryModel = DateQueryModel.getRelativeTimezoneQuery(
        RelativeTimezoneDateTypeEnum.LAST_24_HOURS
    );

    /** Each card takes [current, previous]; the card works the change out from the pair */
    analyzedSeries: ReportSeriesModel[] = [];
    curatedSeries: ReportSeriesModel[] = [];
    curationRateSeries: ReportSeriesModel[] = [];

    /** Epoch ms of the agent's most recent run, or null until it has had one */
    lastCurationActivity: number = null;

    recentActivityChart: Am5DateValueXYChart;
    recentActivitySeries: ReportSeriesModel[] = [];

    /** The agent whose sources the summary below can be broken down by */
    agent: CurationAgentModel;
    sourceOptions: DropdownOptionModel[] = [];
    selectedSourceId: number = null;

    /** Same three metrics as the cards above, narrowed to one source */
    sourceAnalyzedSeries: ReportSeriesModel[] = [];
    sourceCuratedSeries: ReportSeriesModel[] = [];
    sourceRateSeries: ReportSeriesModel[] = [];
    sourceTrendChart: Am5DateValueXYChart;
    sourceTrendSeries: ReportSeriesModel[] = [];

    /** Ad hoc submissions against this agent, newest first */
    adHocHistory: AdHocCurationEntry[] = [];

    /** One menu per ad hoc row: View source is only live on runs that carry their coverage */
    adHocMenus: Map<number, MenuItem[]> = new Map<number, MenuItem[]>();

    /**
     * Row menu shared by both tables at the foot of the dashboard. The split button hands its
     * [data] to the command as event.data, not on the item.
     */
    readonly viewSourceOption: MenuItem = {
        label: this.localizationService.transform('AutomationAgents.card.source.view'),
        command: (event: SplitButtonCommandEvent) => this.viewSource(event.data)
    };
    readonly removeSourceOption: MenuItem = {
        label: this.localizationService.transform('AutomationAgents.card.source.remove'),
        command: (event: SplitButtonCommandEvent) => this.removeSource(event.data)
    };

    @Output() rangeChanged: EventEmitter<DateQueryModel> = new EventEmitter<DateQueryModel>();

    private sourceNames: Map<string, string> = new Map<string, string>();

    /** The agent's ad hoc runs, which say what is left in scope as well as filling the table */
    private adHocRuns: AgentAdhocSourceResponse[] = [];

    /** What the sidebar has ticked, which is all everything below reports on. Null means not narrowed yet. */
    private selectedSourceIds: number[] = null;
    private selectedAdHocIds: number[] = null;

    /** Per source totals over the window, behind the Live Sources activity column */
    /** Both totals per source, which agents-service records together */
    private totalsBySource: Map<number, SourceTotals> = new Map<number, SourceTotals>();

    private summarySubscription: Subscription;
    private sourceSummarySubscription: Subscription;
    private sourceTotalsSubscription: Subscription;

    dateFormat: string;
    private readonly subscriptions: Subscription[] = [];

    constructor(
        private localizationService: LocalizationService,
        private route: ActivatedRoute,
        private curationAgentsService: CurationAgentsService,
        private agentUsageService: AgentUsageService,
        private coreDialogService: CoreDialogService,
        private socialChartTooltipService: SocialChartTooltipService,
        private userStateService: CurrentUserStateService,
        private am5ChartConfigurationService: Am5ChartConfigurationService,
        private am5ChartSettingsService: Am5ChartSettingsService,
        private reportsChartSeriesConverterService: ReportsChartSeriesConverterService
    ) {}

    ngOnInit(): void {
        // Seeded from the shared range, so the tabs agree on the window. All isn't offered here,
        // so a feed left on All opens the dashboard on the last 24 hours instead
        this.curationAgentsService.selectedDateRange$.pipe(take(1)).subscribe(range => {
            if (range && !DateQueryModel.equals(range, DateQueryModel.getAllQuery())) {
                this.selectedRange = range;
            }
        });

        // The id sits on the agent route; this component is one of its sections
        this.subscriptions.push(
            this.route.parent.paramMap.pipe(
                map(params => Number(params.get('agent_id'))),
                filter(id => !!id)
            ).subscribe(id => this.curationAgentsService.setCurrentAgent(id)),

            // The same instance the sidebar lists, so pausing or removing a source there shows here
            combineLatest([
                this.curationAgentsService.currentAgent$,
                this.curationAgentsService.sourceNames$
            ]).subscribe(([agent, names]) => {
                this.agent = agent;
                this.sourceNames = names;
                this.buildSourceOptions(names);
                this.loadUsage();
            }),

            // Unticking a source or an ad hoc run narrows everything the dashboard reports on
            combineLatest([
                this.curationAgentsService.selectedSources$,
                this.curationAgentsService.selectedAdHocEvents$
            ]).subscribe(([selectedSourceIds, selectedAdHocIds]) => {
                this.selectedSourceIds = selectedSourceIds;
                this.selectedAdHocIds = selectedAdHocIds;
                this.buildSourceOptions(this.sourceNames);
                this.loadUsage();
            }),

            this.userStateService.getPreferences().subscribe(prefs => {
                this.dateFormat = prefs.getDateFormatString(true, true, true);
            })
        );
    }

    ngOnDestroy(): void {
        [this.summarySubscription, this.sourceSummarySubscription, this.sourceTotalsSubscription]
            .forEach(subscription => subscription?.unsubscribe());

        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.subscriptions.length = 0;
    }

    sourceSelected(sourceId: number): void {
        this.selectedSourceId = sourceId;
        this.loadSourceSummary();
    }

    /**
     * Sources the agent is pulling from. Every one of them, ticked or not: the checkboxes narrow
     * what the figures report on, not what the agent is configured with.
     */
    get liveSources(): CurationAgentSourceModel[] {
        return (this.agent?.sources ?? []).filter(source => source.isComplete());
    }

    sourceLabel(source: CurationAgentSourceModel): string {
        return this.sourceName(source, this.sourceNames);
    }

    /** Curated against analyzed over the chosen window, as the Activity column reads */
    sourceActivity(source: CurationAgentSourceModel): string {
        const totals = this.totalsBySource.get(source.id);

        return `${totals?.curated ?? 0}/${totals?.analyzed ?? 0}`;
    }

    statusChanged(source: CurationAgentSourceModel, active: boolean): void {
        source.status = active
            ? CurationAgentSourceStatusEnum.active
            : CurationAgentSourceStatusEnum.paused;

        this.curationAgentsService.setSourceStatus(this.agent, source).subscribe();
    }

    /** Lands on the source's own coverage in a new tab, as the monitoring source picker does */
    viewSource(source: CurationAgentSourceModel): void {
        this.curationAgentsService.viewSource(source);
    }

    /** Opens the coverage the run was submitted from */
    /**
     * What a run was submitted over. Resolved as the row is drawn rather than stamped on when the
     * table is built: the names load after the first read, and a stamped label would keep the id
     * it was built with.
     */
    adHocSourceLabel(entry: AdHocCurationEntry): string {
        return this.adHocSourceName(entry?.run);
    }

    /** Whether the run carries the coverage it was submitted over, and so has something to open */
    canViewAdHocSource(entry: AdHocCurationEntry): boolean {
        return this.curationAgentsService.hasAdHocSource(entry?.run);
    }

    viewAdHocSource(entry: AdHocCurationEntry): void {
        this.curationAgentsService.viewAdHocSource(entry?.run);
    }

    /** What the run curated, and what it did with it, in a panel of its own */
    viewAdHocReport(entry: AdHocCurationEntry): void {
        if (!entry?.run) {
            return;
        }

        this.coreDialogService.open(AdHocReportPanelComponent, {
            inputs: {
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                ),
                overlayContainer: document.getElementById('main-content'),
                agent: this.agent,
                run: entry.run
            }
        });
    }

    removeSource(source: CurationAgentSourceModel): void {
        this.curationAgentsService.removeSource(this.agent, source).subscribe(() => {
            this.agent.sources = this.agent.sources.filter(row => row !== source);
            this.buildSourceOptions(this.sourceNames);
        });
    }

    /** Sources are edited as part of the agent, so adding one opens the agent's panel */
    addSource(): void {
        this.coreDialogService.open(CurationAgentPanelComponent, {
            inputs: {
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                ),
                overlayContainer: document.getElementById('main-content'),
                agent: this.agent
            }
        });
    }

    /** Every ad hoc run on the account, opened on this agent */
    openAdHocSettings(): void {
        this.coreDialogService.open(AdHocAgentManagementPanelComponent, {
            inputs: {
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                ),
                overlayContainer: document.getElementById('main-content'),
                agentId: this.agent?.id
            }
        });
    }

    /** Runs the agent by hand over a set of mentions chosen in the panel */
    addAdHocCuration(): void {
        this.coreDialogService.open(AgentSourcePanelComponent, {
            inputs: {
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                ),
                overlayContainer: document.getElementById('main-content'),
                agent: this.agent
            }
        });
    }

    /** Name of the source the summary is currently reporting on, for the heading */
    get selectedSourceLabel(): string {
        return this.sourceOptions.find(option => option.value === this.selectedSourceId)?.label ?? '';
    }

    rangeSelected(range: DateQueryModel): void {
        this.selectedRange = range;
        // The agent's sections report over one window, so the other two follow this
        this.curationAgentsService.setSelectedDateRange(range);
        this.rangeChanged.emit(range);
        this.loadUsage();
    }

    /**
     * Re-reads the page: the agent and its sources through the shared service, and every figure on
     * top of them. The spinner runs for as long as the slowest of those takes.
     */
    refresh(): void {
        if (this.refreshing) {
            return;
        }

        this.refreshing = true;
        this.curationAgentsService.refresh();
        this.loadUsage();
    }

    /** Everything on the page reports over the chosen window, so it is all re-read together */
    private loadUsage(): void {
        this.loadSummary();
        this.loadSourceTotals();
        this.loadSourceSummary();
    }

    /** Sources and ad hoc runs still ticked, which is all the figures report on */
    private usageScope(): { sourceIds: number[]; adHocRuns: AgentAdhocSourceResponse[] } {
        const sourceIds = (this.agent?.sources ?? [])
            .filter(source => source.isComplete() && (!this.selectedSourceIds || this.selectedSourceIds.includes(source.id)))
            .map(source => source.id);

        return { sourceIds: sourceIds, adHocRuns: this.includedAdHocRuns };
    }

    /** The cards, the activity chart and the ad hoc table, which are all agent-wide */
    private loadSummary(): void {
        const agentId = this.agent?.id;

        if (!agentId) {
            return;
        }

        const range = AgentUsageService.reportingRange(this.selectedRange);
        const previous = AgentUsageService.previousPeriod(range);

        // The runs are read first because they say whether any ad hoc usage is still in scope,
        // which the analyzed read has to know before it is made
        this.summarySubscription?.unsubscribe();
        this.summarySubscription = this.curationAgentsService.adHocRuns(agentId).pipe(
            take(1),
            switchMap(runs => {
                this.adHocRuns = runs ?? [];

                const scope = this.usageScope();

                return forkJoin({
                    activity: this.agentUsageService.activityForScope(range, agentId, scope),
                    previousActivity: previous
                        ? this.agentUsageService.activityForScope(previous, agentId, scope)
                        : of(emptyActivity),
                    adHocRuns: of(this.adHocRuns)
                });
            }),
            // The summary is the last of the three reads to finish, so the spinner ends with it
            finalize(() => this.refreshing = false)
        ).subscribe(usage => {
            this.buildCards(
                usage.activity.analyzed,
                usage.activity.curated,
                usage.previousActivity.analyzed,
                usage.previousActivity.curated
            );
            this.buildRecentActivityChart(usage.activity.analyzed, usage.activity.curated, usage.adHocRuns);
            this.buildAdHocHistory(usage.adHocRuns);
            this.updateLastActivity(usage.adHocRuns);
        });
    }

    /** The two totals behind each row of the Live Sources table */
    private loadSourceTotals(): void {
        const agentId = this.agent?.id;
        const sourceIds = this.liveSources.map(source => source.id).filter(id => !!id);

        if (!agentId || !sourceIds.length) {
            this.totalsBySource = new Map<number, SourceTotals>();

            return;
        }

        const range = AgentUsageService.reportingRange(this.selectedRange);

        this.sourceTotalsSubscription?.unsubscribe();
        this.sourceTotalsSubscription = this.agentUsageService.totalsBySource(range, agentId)
            .subscribe(totals => this.totalsBySource = totals);
    }

    private buildCards(
        analyzed: AgentUsageModel,
        curated: AgentUsageModel,
        analyzedPrevious: AgentUsageModel,
        curatedPrevious: AgentUsageModel
    ): void {
        this.analyzedSeries = [
            this.series('AutomationAgents.agent.dashboard.analyzed', analyzed.total),
            this.series('AutomationAgents.agent.dashboard.analyzed', analyzedPrevious.total)
        ];
        this.curatedSeries = [
            this.series('AutomationAgents.agent.dashboard.curated', curated.total),
            this.series('AutomationAgents.agent.dashboard.curated', curatedPrevious.total)
        ];
        this.curationRateSeries = [
            this.series(
                'AutomationAgents.agent.dashboard.curationRate',
                this.curationRate(analyzed.total, curated.total)
            ),
            this.series(
                'AutomationAgents.agent.dashboard.curationRate',
                this.curationRate(analyzedPrevious.total, curatedPrevious.total)
            )
        ];
    }

    /** Share of what the agent looked at that it kept, as a percentage */
    private curationRate(analyzed: number, curated: number): number {
        return analyzed ? (curated / analyzed) * 100 : 0;
    }

    /**
     * The tooltip card the rest of the charts use, minus the drilldown link - nothing on this
     * dashboard drills through to a mention list. amCharts 5 has no {color} placeholder, so the
     * icon is tinted with the line's own colour here.
     */
    private chartTooltip(label: string, value: string, valueName: string, color: string): string {
        return this.socialChartTooltipService.getTooltipHTML(
            '', label, value, valueName, 'fa-solid fa-code-commit', false, '0'
        ).replace('{color}', color);
    }

    /**
     * Analyzed against curated over the window, with a rule at each ad hoc curation.
     *
     * On a date axis, the way the coverage charts are drawn: the buckets carry their own dates, so
     * the axis labels and ends itself from the data rather than from an index the labels are
     * reconstructed off. Ad hoc runs are marked the way a report marks a PR event - a range on that
     * same axis, at the moment the run was submitted.
     */
    private buildRecentActivityChart(
        analyzed: AgentUsageModel,
        curated: AgentUsageModel,
        adHocRuns: AgentAdhocSourceResponse[]
    ): void {
        const bounds = AgentUsageService.bounds(AgentUsageService.reportingRange(this.selectedRange));
        // The interval usage came back in, so the axis steps in the buckets it is plotting
        const granularity = this.activityGranularity(bounds);

        const lines: TrendLine[] = [
            { label: 'AutomationAgents.agent.dashboard.analyzed', color: chartColors.agprBlue, points: this.trendPoints(analyzed) },
            { label: 'AutomationAgents.agent.dashboard.curated', color: chartColors.agprPurple, points: this.trendPoints(curated) }
        ];

        this.recentActivitySeries = lines.map(line => this.trendSeries(line));

        // One axis for both counts, titled on the left - the converter's default for a comparison
        const chartSeries = this.trendChartSeries(this.recentActivitySeries, lines, granularity, () => '#,###');
        chartSeries[0].axisTitleText = this.localizationService.transform('AutomationAgents.agent.dashboard.activity.mentions');

        this.recentActivityChart = new Am5DateValueXYChart(
            chartSeries,
            this.trendChartConfiguration('agentRecentActivityChart'),
            this.trendChartSettings(this.recentActivitySeries, lines, granularity, this.adHocEvents(adHocRuns, bounds))
        );
    }

    /** The bucket the window is reported in, as the date axis's own interval */
    private activityGranularity(bounds: UsageBounds): DateGranularityModel {
        const bucket = bounds ? AgentUsageService.bucketMs(bounds) : hourlyBucketMs;

        return bucket <= hourlyBucketMs
            ? { timeUnit: 'hour', count: 1 }
            : { timeUnit: 'day', count: Math.max(1, Math.round(bucket / (24 * hourlyBucketMs))) };
    }

    /**
     * Each ad hoc run inside the window as a rule on the date axis, at the moment it was submitted.
     * A run outside it is left off rather than pinned to an end, which would read as a run that
     * happened on the day the window opens.
     */
    private adHocEvents(adHocRuns: AgentAdhocSourceResponse[], bounds: UsageBounds): PREventModel[] {
        const label = this.localizationService.transform('AutomationAgents.agent.dashboard.activity.adHoc');

        return (adHocRuns ?? [])
            .filter(run => !bounds || (run.createdAt >= bounds.start && run.createdAt <= bounds.end))
            .map(run => {
                const date = new DateQueryModel();
                date.absoluteDate = new Date(run.createdAt);

                return {
                    name: label,
                    date: date,
                    model: null,
                    color: chartColors.agprOrange,
                    type: null,
                    typeLabel: null,
                    tooltipHTML: this.adHocEventTooltip(run, label)
                };
            });
    }

    /** The run's day and what it analyzed and curated, in the same card as the lines' own tooltips */
    private adHocEventTooltip(run: AgentAdhocSourceResponse, label: string): string {
        const row = (key: string, color: string, count: number): string => this.socialChartTooltipService
            .getMultiSeriesSharedTooltipHTML(
                '', (count ?? 0).toLocaleString(), this.localizationService.transform(key), 'fa-solid fa-code-commit', false
            )
            .replace('{color}', color);

        return `
            <div style="margin: 10px; font-size: 0.875rem; min-width: 220px; color: ${chartColors.agprBlack};">
                <div style="border-bottom: 1px solid #ced4da; padding-bottom: 5px;">
                    <b style="font-weight: 600">${moment(run.createdAt).format('YYYY-MM-DD')}</b>
                </div>
                <div style="margin-top: 10px; font-style: italic;">${label}</div>
                ${row('AutomationAgents.agent.dashboard.analyzed', chartColors.agprBlue, run.evaluatedMentions)}
                ${row('AutomationAgents.agent.dashboard.curated', chartColors.agprPurple, run.curatedMentions)}
            </div>
        `;
    }

    /** One entry per complete source, named from the shared lookup */
    private buildSourceOptions(names: Map<string, string>): void {
        const sources = this.agent?.sources?.filter(source => source.isComplete()) ?? [];

        this.sourceOptions = sources.map(source => new DropdownOptionModel(
            this.sourceName(source, names),
            source.id
        ));

        // Default to the first source, and fall back to it if the chosen one has gone away
        if (!this.sourceOptions.some(option => option.value === this.selectedSourceId)) {
            this.selectedSourceId = this.sourceOptions[0]?.value ?? null;
        }
    }

    /**
     * What a run was submitted over: the source's own name where it was submitted as one, and the
     * kind otherwise - a run built from a query or a list of mentions has no source behind it.
     */
    private adHocSourceName(run: AgentAdhocSourceResponse): string {
        const named = run?.sourceType && run?.sourceRef
            ? this.sourceNames.get(sourceNameKey(run.sourceType, run.sourceRef))
            : null;

        return named ?? this.localizationService.transform(`AutomationAgents.adHoc.kind.${run.kind}`);
    }

    private sourceName(source: CurationAgentSourceModel, names: Map<string, string>): string {
        const id = source.dataSource?.id;

        return names.get(sourceNameKey(source.dataSource?.type, id))
            ?? source.dataSource?.dataSourceName
            ?? String(id ?? '');
    }

    /** The same three metrics as the summary cards, narrowed to one source, beside their trend */
    private loadSourceSummary(): void {
        const agentId = this.agent?.id;
        const sourceId = this.selectedSourceId;

        if (!agentId || !sourceId) {
            this.sourceAnalyzedSeries = [];
            this.sourceCuratedSeries = [];
            this.sourceRateSeries = [];
            this.sourceTrendChart = null;
            this.sourceTrendSeries = [];

            return;
        }

        const range = AgentUsageService.reportingRange(this.selectedRange);
        const previous = AgentUsageService.previousPeriod(range);

        this.sourceSummarySubscription?.unsubscribe();
        this.sourceSummarySubscription = forkJoin({
            activity: this.agentUsageService.activity(range, agentId, sourceId),
            previousActivity: previous
                ? this.agentUsageService.activity(previous, agentId, sourceId)
                : of(emptyActivity)
        }).subscribe(usage => {
            const { analyzed, curated } = usage.activity;
            const previousTotals = usage.previousActivity;

            this.sourceAnalyzedSeries = [
                this.series('AutomationAgents.agent.dashboard.analyzed', analyzed.total),
                this.series('AutomationAgents.agent.dashboard.analyzed', previousTotals.analyzed.total)
            ];
            this.sourceCuratedSeries = [
                this.series('AutomationAgents.agent.dashboard.curated', curated.total),
                this.series('AutomationAgents.agent.dashboard.curated', previousTotals.curated.total)
            ];
            this.sourceRateSeries = [
                this.series(
                    'AutomationAgents.agent.dashboard.curationRate',
                    this.curationRate(analyzed.total, curated.total)
                ),
                this.series(
                    'AutomationAgents.agent.dashboard.curationRate',
                    this.curationRate(previousTotals.analyzed.total, previousTotals.curated.total)
                )
            ];

            this.buildSourceTrendChart(analyzed, curated);
        });
    }

    private buildSourceTrendChart(analyzed: AgentUsageModel, curated: AgentUsageModel): void {
        const granularity = this.activityGranularity(
            AgentUsageService.bounds(AgentUsageService.reportingRange(this.selectedRange))
        );

        const ratePoints: TrendPoint[] = analyzed.points.map((point, index) => ({
            timestamp: point.timestamp,
            value: this.curationRate(point.count, curated.points[index]?.count ?? 0)
        }));

        const lines: TrendLine[] = [
            { label: 'AutomationAgents.agent.dashboard.analyzed', color: chartColors.agprBlue, points: this.trendPoints(analyzed) },
            { label: 'AutomationAgents.agent.dashboard.curated', color: chartColors.agprPurple, points: this.trendPoints(curated) },
            { label: 'AutomationAgents.agent.dashboard.curationRate', color: chartColors.agprLightBlue, points: ratePoints }
        ];

        this.sourceTrendSeries = lines.map(line => this.trendSeries(line));

        // Multi axis: the first series takes the left hand axis, the rest get their own on the right,
        // each tinted to match its line - the same treatment as Social's Summary by Social Network
        const chartSeries = this.trendChartSeries(
            this.sourceTrendSeries, lines, granularity, index => index === 2 ? '#.#' : '#,###'
        );
        chartSeries.forEach((series, index) => {
            series.requiresAxis = true;
            series.axisIndex = index;
            series.hideAxisGrid = index !== 0;
        });
        chartSeries[0].axisTitleText = this.localizationService.transform('AutomationAgents.agent.dashboard.activity.mentions');
        chartSeries[2].percentageValueAxis = true;

        this.sourceTrendChart = new Am5DateValueXYChart(
            chartSeries,
            this.trendChartConfiguration('agentSourceTrendChart'),
            this.trendChartSettings(this.sourceTrendSeries, lines, granularity, [])
        );
    }

    /** This chart is on a date axis, so its x values are the buckets' timestamps */
    private trendPoints(usage: AgentUsageModel): TrendPoint[] {
        return usage.points.map(point => ({ timestamp: point.timestamp, value: point.count }));
    }

    /**
     * The lines as a report's coverage comparison draws them, all on the first axis unless the
     * caller gives one its own. The tooltip is the dashboard's card, dated to the bucket the axis
     * steps in.
     */
    private trendChartSeries(
        reportSeries: ReportSeriesModel[],
        lines: TrendLine[],
        granularity: DateGranularityModel,
        numberFormat: (index: number) => string
    ): ChartSeries[] {
        const dateFormat = granularity.timeUnit === 'hour' ? 'yyyy-MM-dd HH:mm' : 'yyyy-MM-dd';
        const chartSeries = this.reportsChartSeriesConverterService.getDateValueChartSeries(
            reportSeries, ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON, ChartSpecificTypesEnum.LINE, null
        );

        chartSeries.forEach((series, index) => {
            // The lines carry no report metric to suffix the export label with
            series.exportLabel = series.label;
            series.tooltipHTML = this.chartTooltip(
                `{valueX.formatDate('${dateFormat}')}`,
                `{valueY.formatNumber('${numberFormat(index)}')}`,
                series.label,
                lines[index].color
            );
        });

        return chartSeries;
    }

    /** A report's coverage comparison, without the header or scrollbar - the dashboard titles its charts */
    private trendChartConfiguration(id: string): Am5DateValueXYChartConfiguration {
        const config = this.am5ChartConfigurationService.getChartConfiguration(
            id, ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON
        ) as Am5DateValueXYChartConfiguration;

        config.header.hasHeader = false;
        config.useScrollbar = false;
        config.noContent = {
            isMessageStyle: true,
            label: this.localizationService.transform('AutomationAgents.notAvailable')
        };

        return config;
    }

    private trendChartSettings(
        reportSeries: ReportSeriesModel[],
        lines: TrendLine[],
        granularity: DateGranularityModel,
        events: PREventModel[]
    ): Am5DateValueXYChartSettings {
        const styles = new ChartStylesModel();
        styles.colorMap = Object.fromEntries(lines.map((line, index) => ['seriesColor' + index, line.color]));
        styles.chartDisplaySettings = new ChartDisplaySettingsModel({
            title: false,
            dateRange: false,
            sourceLink: false,
            previousPeriod: false,
            multiSource: false,
            trendIndicators: false
        });

        const displayElement = new ChartDisplayElementModel('', reportSeries, styles);
        displayElement.chartSpecificType = ChartSpecificTypesEnum.LINE;
        displayElement.timeInterval = ChartTimeIntervalsEnum.DYNAMIC;
        displayElement.prevents = events;

        return {
            ...this.am5ChartSettingsService.getChartSettings(ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON, displayElement),
            // Each point is already a bucket of the interval usage came back in
            groupData: false,
            dateGranularity: granularity
        } as Am5DateValueXYChartSettings;
    }

    /** Past ad hoc runs, newest first. Runs carry no source - the submission itself is the source */
    private buildAdHocHistory(adHocRuns: AgentAdhocSourceResponse[]): void {
        // Every run, ticked or not - the list is the agent's history rather than the current scope
        this.adHocHistory = (adHocRuns ?? [])
            .slice()
            .sort((first, second) => second.createdAt - first.createdAt)
            .map(run => ({
                date: run.createdAt,
                curated: run.curatedMentions,
                analyzed: run.totalMentions,
                inProgress: run.status === AgentAdhocSourceStatusEnum.inProgress,
                run: run
            }));

        this.adHocMenus = new Map<number, MenuItem[]>(
            this.adHocHistory.map(entry => [entry.run.id, this.adHocRowOptions(entry)])
        );
    }

    /** A run with no coverage recorded against it has nothing to open, so that item is disabled */
    private adHocRowOptions(entry: AdHocCurationEntry): MenuItem[] {
        return [
            {
                label: this.localizationService.transform('AutomationAgents.card.source.view'),
                disabled: !this.canViewAdHocSource(entry),
                command: (event: SplitButtonCommandEvent) => this.viewAdHocSource(event.data)
            },
            {
                label: this.localizationService.transform('AutomationAgents.agent.dashboard.adHoc.viewReport'),
                command: (event: SplitButtonCommandEvent) => this.viewAdHocReport(event.data)
            }
        ];
    }

    /** The runs the sidebar still has ticked, which is what the ad hoc figures are counted from */
    private get includedAdHocRuns(): AgentAdhocSourceResponse[] {
        return this.adHocRuns.filter(run => !this.selectedAdHocIds || this.selectedAdHocIds.includes(run.id));
    }

    /** The most recent thing the agent did: a scheduled scan, or a run submitted by hand */
    private updateLastActivity(adHocRuns: AgentAdhocSourceResponse[]): void {
        const timestamps = [
            ...(this.agent?.sources ?? []).map(source => source.lastScannedAt),
            ...(adHocRuns ?? []).map(run => run.createdAt)
        ].filter(timestamp => !!timestamp);

        this.lastCurationActivity = timestamps.length ? Math.max(...timestamps) : null;
    }

    /** One line of the source trend, with its own axis colour */
    /**
     * Feeds one line to the chart, in the shape the shared merger keys by - the date, and the
     * line's label as the source it is grouped under
     */
    private trendSeries(line: TrendLine): ReportSeriesModel {
        const series = new ReportSeriesModel();
        series.label = this.localizationService.transform(line.label);
        series.color = line.color;
        series.dataProvider.next(line.points.map(point => ({
            mappingKeys: {
                [ReportsDataMappingKeysEnum.DATE]: point.timestamp,
                [ReportsDataMappingKeysEnum.SOURCE]: series.label
            },
            value: point.value
        }) as DataPointModel));

        return series;
    }

    /** A one point series, which is all a summary card reads */
    private series(label: string, count: number): ReportSeriesModel {
        const series = new ReportSeriesModel();
        series.label = label;
        series.dataProvider.next([{ count: count }]);

        return series;
    }

    rowOptions(source: CurationAgentSourceModel) {
        const options: MenuItem[] = [
            this.removeSourceOption
        ];

        if (source.dataSource.type !== MonitoringSeriesTypeEnum.socialTopic) {
            options.unshift(this.viewSourceOption);
        }

        return options;
    }
}
